import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner } from 'react-bootstrap'

import useSessionGate from '../hooks/useSessionGate'
import { getDashboardInvitationPreview, acceptDashboardInvitation } from '../services/dashboardInvitationApi'
import LoginModal from '../components/LoginModal'
import RegisterModal from '../components/RegisterModal'
import ForgotModal from '../components/ForgotModal'
import { CHANGE_FORGOT_PASSWORD_RESET } from '../constants/userConstants'
import './ShareLinkLandingScreen.css'

const PENDING_TOKEN_KEY = 'pendingShareToken'

const GENERIC_INVALID_MESSAGE = 'This invite link is invalid, expired, or has been revoked.'

// Screen state machine, independent of the session gate:
// 'loading-preview' | 'invalid' | 'ready' | 'accepting' | 'already-owner' | 'accept-error'
function ShareLinkLandingScreen() {
  // The token lives in the URL fragment because the app uses HashRouter
  // (`/#/share/dashboard/:token`). Fragments are never sent to the server,
  // so the token stays out of access logs and Referer headers — a real
  // security property. Do not "fix" this into a real path/query param that
  // a server-side router would see.
  const { token } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const gate = useSessionGate()
  const access = useSelector(state => state.userLogin?.userInfo?.access)

  const [screenState, setScreenState] = useState('loading-preview')
  const [preview, setPreview] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  const [loginModal, setLoginModal] = useState(false)
  const [registerModal, setRegisterModal] = useState(false)
  const [forgotModal, setForgotModal] = useState(false)

  // Keep the token available across a full sign-up + email-verification
  // detour (register -> check email -> activate -> log in from somewhere
  // else entirely). App.jsx watches for this key and redirects back here
  // right after a successful login, anywhere in the app.
  useEffect(() => {
    if (!gate.isAuthenticated && !gate.isChecking && token) {
      sessionStorage.setItem(PENDING_TOKEN_KEY, token)
    }
  }, [gate.isAuthenticated, gate.isChecking, token])

  const acceptInvitation = useCallback(async () => {
    setScreenState('accepting')
    try {
      const data = await acceptDashboardInvitation(token, access)
      sessionStorage.removeItem(PENDING_TOKEN_KEY)
      navigate(`/dashboard/${data.dashboard_code_id}`)
    } catch (error) {
      const status = error.response?.status
      if (status === 400 && error.response?.data?.detail === 'already_owner') {
        setScreenState('already-owner')
      } else if (status === 404) {
        setScreenState('invalid')
      } else if (status === 429) {
        setErrorMessage("You've hit the limit for invitation attempts. Please try again in a bit.")
        setScreenState('accept-error')
      } else if (status === 401) {
        // Token refresh must have failed mid-flow between the preview load
        // and the accept click. Fall back to the signed-out state; the
        // token is preserved because it's still in the URL and in
        // sessionStorage.
        gate.recheck()
      } else {
        setErrorMessage('Something went wrong accepting this invitation. Please try again.')
        setScreenState('accept-error')
      }
    }
  }, [token, access, navigate, gate])

  useEffect(() => {
    if (!gate.isAuthenticated || !token) return

    let active = true
    setScreenState('loading-preview')

    getDashboardInvitationPreview(token, access)
      .then(data => {
        if (!active) return
        setPreview(data)
        if (data.already_member) {
          // Already a member: skip the invitation card and redeem straight
          // away. Accept is idempotent for an existing grant (no use-count
          // burn), so this is safe to do without the user clicking anything.
          acceptInvitation()
        } else {
          setScreenState('ready')
        }
      })
      .catch(error => {
        if (!active) return
        const status = error.response?.status
        if (status === 401) {
          gate.recheck()
        } else {
          setScreenState('invalid')
        }
      })

    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.isAuthenticated, token, access])

  // --- Still checking the session ---
  if (gate.isChecking) {
    return (
      <main className='share-invite-surface d-flex justify-content-center align-items-center'>
        <Spinner animation='border' as={'span'} style={{ width: '24px', height: '24px' }} />
      </main>
    )
  }

  // --- No usable session: sign-in / sign-up invitation ---
  if (!gate.isAuthenticated) {
    return (
      <main className='share-invite-surface'>
        <div className='share-invite-card'>
          <h1 className='p-title'>You've been invited to a shared dashboard</h1>
          <p className='p-paragraph'>Sign in or create an account to view the invitation and accept access.</p>
          <div className='share-invite-actions'>
            <button type='button' className='login-button' onClick={() => setLoginModal(true)}>Log In</button>
            <button type='button' className='share-invite-secondary-button' onClick={() => setRegisterModal(true)}>Sign Up</button>
          </div>
        </div>

        {loginModal && (
          <LoginModal
            isViewer
            closeCallback={() => setLoginModal(false)}
            registerCallback={() => { setLoginModal(false); setRegisterModal(true) }}
            forgotCallback={() => {
              dispatch({ type: CHANGE_FORGOT_PASSWORD_RESET })
              setLoginModal(false)
              setForgotModal(true)
            }}
          />
        )}

        {registerModal && (
          <RegisterModal
            closeCallback={() => setRegisterModal(false)}
            loginCallback={() => { setRegisterModal(false); setLoginModal(true) }}
          />
        )}

        {forgotModal && (
          <ForgotModal closeCallback={() => setForgotModal(false)} />
        )}
      </main>
    )
  }

  // --- Authenticated: preview / accept flow ---
  if (screenState === 'loading-preview' || screenState === 'accepting') {
    return (
      <main className='share-invite-surface d-flex justify-content-center align-items-center'>
        <Spinner animation='border' as={'span'} style={{ width: '24px', height: '24px' }} />
      </main>
    )
  }

  if (screenState === 'invalid') {
    return (
      <main className='share-invite-surface'>
        <div className='share-invite-card'>
          <h1 className='p-title'>Invite link unavailable</h1>
          <p className='p-paragraph'>{GENERIC_INVALID_MESSAGE}</p>
        </div>
      </main>
    )
  }

  if (screenState === 'already-owner') {
    return (
      <main className='share-invite-surface'>
        <div className='share-invite-card'>
          <h1 className='p-title'>This is your own dashboard</h1>
          <p className='p-paragraph'>You already own this dashboard, so there's nothing to accept.</p>
          <button type='button' className='login-button' onClick={() => navigate('/dashboard')}>Go to my dashboards</button>
        </div>
      </main>
    )
  }

  if (screenState === 'accept-error') {
    return (
      <main className='share-invite-surface'>
        <div className='share-invite-card'>
          <h1 className='p-title'>Couldn't accept this invitation</h1>
          <p className='p-paragraph'>{errorMessage}</p>
          <button type='button' className='login-button' onClick={acceptInvitation}>Try again</button>
        </div>
      </main>
    )
  }

  if (screenState === 'ready' && preview) {
    return (
      <main className='share-invite-surface'>
        <div className='share-invite-card'>
          <h1 className='p-title'>You've been invited</h1>
          <p className='p-paragraph'>
            <strong>{preview.owner_display_name}</strong> invited you to {preview.role === 'viewer' ? 'view' : 'edit'} the dashboard <strong>{preview.dashboard_name}</strong>.
          </p>
          <p className='share-invite-role'>Role granted: {preview.role}</p>
          <button type='button' className='login-button' onClick={acceptInvitation}>Accept</button>
        </div>
      </main>
    )
  }

  return null
}

export default ShareLinkLandingScreen
